import { ConfigService, StorageService, formatDate, DateFormats, Logger } from '@nextgen-web-framework/core';
import type { Session } from '@nextgen-web-framework/all';
import { parsePhoneNumber as parsePhone } from 'libphonenumber-js/min';
import { DynamicObjectInterface, Citizenship, FunctionType } from '@commerce-profile-webcomponents/sdk';
import logObject from '../../logObject';
import { DEFAULT_LOCALE } from './Constants';

export const EmailRegex = /^[\w.%+-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/;
export const TextRegex = /^[a-zA-Z\d'-.]*(?: [a-zA-Z\d'-.]+)*$/;
export const StandardPhoneRegex = /^[0-9()+]+$/;

// Text format
// https://amwaycloud.atlassian.net/wiki/spaces/NEX/pages/373557158/Field+Validation+Rules
export const StandardTextRegex =
  /^(?!.*\s{2,})(?!.*\.{2,})(?!.*,{2,})(?!.*\+{2,})(?!.*#{2,})(?!^\s)(?!.*\s$)[^@#$%*()+={}\\[\];:"<>?\\|/]*$/;

export const IDNumberRegex = /^[a-zA-Z\d]*$/;

export const isEmail = (email: string) => EmailRegex.test(String(email).toLowerCase());

export const numberRegex = /^\d*$/;
export const isNumber = (n: string) => numberRegex.test(n.toString());

export const extractPhoneNumber = (phoneNumber: string) => {
  const defaultObject = { dialCode: '', number: phoneNumber };
  try {
    const numbers = parsePhone(phoneNumber);
    if (numbers) {
      return {
        dialCode: `+(${numbers.countryCallingCode})`,
        number: numbers.nationalNumber,
      };
    } else {
      return defaultObject;
    }
  } catch {
    // Return default object if there is an error, no additional handling required
    return defaultObject;
  }
};

export const addLeadingZero = (phoneNumber?: DynamicObjectInterface) => {
  const formattedPhoneNumber = parseString(phoneNumber);
  if (formattedPhoneNumber) {
    return formattedPhoneNumber.startsWith('0') ? formattedPhoneNumber : `0${formattedPhoneNumber}`;
  }
  return formattedPhoneNumber;
};

export const removePhoneLeadingZero = (phoneNumber?: string) => {
  const phone = parseString(phoneNumber);
  return phone.startsWith('0') ? phone.substring(1) : phone;
};

export const parsePhoneNumber = (dialCode: string, phoneNumber: string) => {
  // Check if the phone number starts with a zero
  const modifiedPhoneNumber = removePhoneLeadingZero(phoneNumber);
  return `${dialCode}${modifiedPhoneNumber}`.replace(/[()]/g, '');
};

export const parseString = (value?: DynamicObjectInterface, value2?: DynamicObjectInterface): string =>
  value || value2 || '';
export const parseNumber = (value?: DynamicObjectInterface, defaultValue?: number): number =>
  value ?? defaultValue ?? 0;
export const parseArray = (value?: DynamicObjectInterface[]): DynamicObjectInterface[] =>
  Array.isArray(value) ? value : [];
export const parseObject = (value?: DynamicObjectInterface): DynamicObjectInterface =>
  value ?? ({} as DynamicObjectInterface);

export const ifThenElse = (
  condition: string | boolean | undefined,
  thenValue: DynamicObjectInterface,
  elseValue?: DynamicObjectInterface,
) => (condition ? thenValue : elseValue);

// To replace ifThenElse in future
export const iff = (condition: boolean, thenValue: DynamicObjectInterface, elseValue?: DynamicObjectInterface) =>
  ifThenElse(condition, thenValue, elseValue);

export const castTranslation = (t: (key: string) => string, prefix: string, key: string) => {
  const translationKey = `${prefix}${key}`;
  const label = t(translationKey);
  return label === translationKey ? key : label;
};

export const processLabelText = (
  t: (key: string) => string,
  existingText: string,
  isRequired: DynamicObjectInterface,
) => (isRequired ? existingText : `${existingText}${t('profile.common.optional')}`);

export const localeCorrection = (locale: string): string => {
  // We need to check if the locale is already in the format of 'en-us', 'en-gb' or 'th-th'
  const { NEXT_PUBLIC_COUNTRY } = ConfigService.getConfigs();
  if (locale.split('-').length > 1 || locale === DEFAULT_LOCALE) {
    // default for en
    return locale;
  } else {
    return `${locale}-${NEXT_PUBLIC_COUNTRY}`;
  }
};

export const getSiteFallbackLocale = (): string => {
  const { NEXT_FALLBACK_LANGUAGE } = ConfigService.getConfigs();
  const locale = String(NEXT_FALLBACK_LANGUAGE);
  const [lang] = locale.split('-');
  return lang;
};

/**
 * Formats a given date string based on the specified locale.
 *
 * @param date - The date string to be formatted.
 * @param locale - Optional. The locale to use for formatting the date. Defaults to the user's locale if not provided.
 * @param dateFormat - Optional. The format to use for the date. If not provided, the default date format from the configuration will be used.
 * @returns The formatted date string.
 *
 * @remarks
 * This function depends on the following configurations from `ConfigService`:
 * - `DEFAULT_DATE_LOCALE`: The default locale to use if no locale is provided.
 */
export const formatDateByLocale = (
  date: string,
  locale?: string,
  dateFormat = DateFormats.SHORT_DATE,
  fallbackDateLocale = getSiteFallbackLocale(),
): string => {
  if (!date) {
    return '';
  }

  // if using english as fallback (en-us), use the default date locale
  const adjustedLocale = locale === getSiteFallbackLocale() ? fallbackDateLocale : locale;

  return formatDate(
    date,
    dateFormat, // To show short/medium/long date format
    adjustedLocale, // Date display sequence
  );
};

export const getMarketLocale = (): string => {
  const { NEXT_PUBLIC_LANGUAGE } = ConfigService.getConfigs();

  const defaultLocale = String(NEXT_PUBLIC_LANGUAGE);

  if (defaultLocale) {
    return defaultLocale;
  } else {
    return getSiteFallbackLocale(); // fallback to en
  }
};

export const getDateLocaleByCitizenship = (
  citizenship?: string,
  citizenshipCountryCode?: string,
  fallbackDateLocale = getSiteFallbackLocale(),
): string => {
  // Use this to get the locale based on the citizenship in profile
  // The date will be formatted based on the citizenship, not by site language
  const { NEXT_PUBLIC_COUNTRY } = ConfigService.getConfigs();

  return citizenshipCountryCode?.toLowerCase() === String(NEXT_PUBLIC_COUNTRY)?.toLowerCase() ||
    citizenship === Citizenship.Citizen
    ? getMarketLocale()
    : fallbackDateLocale;
};

export const getCookieData = () => {
  const cookie = StorageService.getCookieStorage();

  const userCookie = decodeURIComponent(cookie.retrieve('user-info') || '{}');

  if (userCookie.includes('id')) {
    const cookieData: DynamicObjectInterface = {};
    userCookie.split(';').forEach((pair) => {
      if (pair) {
        const [key, value] = pair.split('=');
        if (key === 'act' && value) {
          cookieData.act = JSON.parse(value);
        } else if (key === 'accounts' && value) {
          cookieData.accounts = JSON.parse(value);
        } else {
          cookieData[key as keyof Omit<Session, 'act' | 'accounts'>] = value;
        }
      }
    });
    return cookieData;
  }
  return {};
};

export const getDayOrdinal = (t: FunctionType, day: number) => {
  switch (day) {
    case 1:
    case 21:
    case 31:
      return t('profile.common.ordinal.first', { day });
    case 2:
    case 22:
      return t('profile.common.ordinal.second', { day });
    case 3:
    case 23:
      return t('profile.common.ordinal.third', { day });
    default:
      return t('profile.common.ordinal.other', { day }); // Default ordinal suffix (e.g., "th" in English)
  }
};

export const extractDate = (t: FunctionType, date?: Date | null) => {
  if (date) {
    const month = date.getMonth();
    const day = date.getDate();
    const monthList = t('profile.common.months').split(',');
    const dayOrdinal = getDayOrdinal(t, day);
    return { day: day, month: month + 1, dayText: dayOrdinal, monthText: monthList[month] };
  }
  return {
    day: 1,
    month: 1,
    dayText: '',
    monthText: '',
  };
};

/**
 * Retrieves the current environment configuration.
 * To control feature that available in different environment
 * Use case: new feature availble in dev env but not in qa env
 *
 * @returns {string} The environment configuration as a string.
 *
 * @example
 * // Example 1: Using in master data
 * const data = {
 *   env: ['tha-qa']
 * };
 * if (data.env.includes(getEnv())) {
 *   // Control based on environment
 * }
 *
 * @example
 * // Example 2: Conditional control for feature availability
 * if (getEnv() !== MARKET_ENV_TYPE.THA_PROD) {
 *   // Code to disable features in higher environments until final release
 * }
 */
export const getEnv = () => {
  const { NEXT_SSR_BASE_PATH } = ConfigService.getConfigs();
  return String(NEXT_SSR_BASE_PATH);
};

export const siteCountry = () => {
  const { NEXT_PUBLIC_COUNTRY } = ConfigService.getConfigs();
  return parseString(NEXT_PUBLIC_COUNTRY);
};

export const defaultApiErrorHandler = async <TResponse>(callback: () => Promise<TResponse>): Promise<TResponse> =>
  await Promise.resolve(callback());

export const resizeByScrollHeight = (objectId: string) => {
  const bodyHeight = document.body.scrollHeight;
  const targetObject = document.getElementById(objectId);
  targetObject?.style.setProperty('height', `${bodyHeight}px`);
};

export const resizeByBreakpoint = (objectId: string, isMobile?: boolean, mobileObjectId?: string) => {
  resizeByScrollHeight(objectId);
  if (isMobile && mobileObjectId) {
    resizeByScrollHeight(mobileObjectId);
  }
};

export const isValidDateString = (str: string): boolean => {
  const logger = Logger.getInstance(logObject('helper.ts', 'isValidDateString'));
  try {
    const date = new Date(str);
    return !isNaN(date.getTime());
  } catch {
    logger.log('Invalid date string:', str);
    return false;
  }
};

export const parseDate = (date: string): Date => {
  const logger = Logger.getInstance(logObject('helper.ts', 'parseDate'));
  if (isValidDateString(date)) {
    return new Date(date);
  }
  logger.log('Invalid date string:', date);
  return new Date(); // Fallback to current date if parsing fails
};

/**
 * Parses a date string and adjusts it to the configured local timezone.
 *
 * - For strings in `yyyy-mm-dd` format, the function treats the date as being in the target timezone
 *   specified by `NEXT_PUBLIC_TIMEZONE` from `ConfigService.getConfigs()`. It calculates the timezone offset
 *   and adjusts the UTC time so that, when converted to the target timezone, it displays the correct date.
 * - If no timezone is specified, it parses the date as a local date.
 * - For other formats (e.g., ISO strings), it returns the input value as-is.
 *
 * @param dateStr - The date string to parse (e.g., '2024-06-01').
 * @returns The ISO string representation of the adjusted date, or the original string for non-standard formats.
 *
 * @remarks
 * This function is useful for ensuring that date strings are interpreted correctly according to the site's configured timezone,
 * especially when working with date-only strings that may otherwise be interpreted as UTC.
 *
 * @example
 * ```typescript
 * // With timezone configured
 * parseLocalDate('2024-06-01'); // Returns ISO string adjusted for NEXT_PUBLIC_TIMEZONE
 *
 * // Without timezone configured
 * parseLocalDate('2024-06-01'); // Returns ISO string for local date
 *
 * // For ISO string input
 * parseLocalDate('2024-06-01T12:00:00Z'); // Returns input value
 * ```
 */

export const parseLocalDate = (dateStr: string) => {
  const { NEXT_PUBLIC_TIMEZONE } = ConfigService.getConfigs();

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // For yyyy-mm-dd format, treat the date as being in the target timezone
    if (NEXT_PUBLIC_TIMEZONE) {
      // We need to create a UTC time that when converted to target timezone shows the correct date
      // Strategy: Add the timezone offset hours to the UTC time

      const baseDate = new Date(`${dateStr}T00:00:00.000Z`);

      // Get the timezone offset in hours for the specific date and timezone
      const tempDate = new Date(baseDate);

      // Create two dates: one in UTC, one in target timezone, both at same moment
      const utcFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      } as Intl.DateTimeFormatOptions);

      const targetFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: NEXT_PUBLIC_TIMEZONE,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      } as Intl.DateTimeFormatOptions);

      const utcHour = parseInt(utcFormatter.format(tempDate).split(':')[0]);
      const targetHour = parseInt(targetFormatter.format(tempDate).split(':')[0]);

      // Calculate offset in hours (how many hours to add to UTC to get target time)
      let offsetHours = targetHour - utcHour;
      if (offsetHours > 12) {
        offsetHours -= 24;
      }
      if (offsetHours < -12) {
        offsetHours += 24;
      }

      // Add the offset hours to our base UTC time
      // If target timezone is UTC-5, we need to add 5 hours to UTC to compensate
      const adjustedDate = new Date(baseDate.getTime() + Math.abs(offsetHours) * 60 * 60 * 1000);
      return adjustedDate.toISOString();
    } else {
      // If no timezone specified, just parse as local date
      const tempDate = new Date(`${dateStr}T00:00:00`);
      return tempDate.toISOString();
    }
  } else {
    // For ISO string or other formats, use value -- original version
    return dateStr;
  }
};

export const convertToLocalDate = (date: string): Date => {
  const parsedDate = parseDate(date);

  try {
    // Fix the format to english us date to ensure the correct conversion MMM DD, YYYY
    const localTimeZoneDate = formatDate(parsedDate?.toISOString(), DateFormats.LONG_DATE, 'en-US');

    const finalDate = new Date(localTimeZoneDate);

    if (isNaN(finalDate.getTime())) {
      return parsedDate; // fallback to original parsed date if final conversion failed
    }

    return finalDate;
  } catch {
    return parsedDate; // Fallback to original date if conversion fails
  }
};

export const hasContent = (html: string): boolean => {
  try {
    const parsed = new DOMParser().parseFromString(html || '', 'text/html');
    const text = parsed.body.textContent?.trim();
    return !!text;
  } catch {
    // If parsing fails, we assume there is no content
    return false;
  }
};
